import React, { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import BaseModal from "./BaseModal";
import PasswordPolicyChecker from "../PasswordPolicyChecker";
import { changeAccountDetail } from "../../api/restaurantApi";
import "../../css/BaseModal.css";
import "../../css/ChangeAccountDetailsModal.css";

/**
 * A modal component for changing account details in the admin profile page.
 *
 * @component
 * @param props - Component props.
 * @param {Boolean} props.isOpen - Controls whether the modal is open or closed.
 * @param {Function} props.onRequestClose - Callback function triggered to close the modal, passed down to BaseModal.
 * @param {String} [props.width='auto'] - Optional width of the modal content, defaults to 'auto' if not provided.
 * @param {Number} props.restaurantId - A Number representing the id of the restaurant.
 * @param {String} props.detailToChange - A String representing the account detail to change.
 * @returns {JSX.Element} The rendered ChangeAccountDetailsModal component.
 */
const ChangeAccountDetailsModal = ({
  isOpen,
  onRequestClose,
  width,
  restaurantId,
  detailToChange,
}) => {
  const navigate = useNavigate();
  const [newChange, setNewChange] = useState("");
  const [confirmNewChange, setConfirmNewChange] = useState("");
  const [error, setError] = useState("");
  const [isPasswordValid, setIsPasswordValid] = useState(false);
  const isMobile = useSelector((state) => state.mobile.isMobile);

  const checkWidth = isMobile ? "80%" : width;

  // handle updated password validity from PasswordPolicy component
  const updatePasswordValidity = useCallback(
    (isValid) => {
      setIsPasswordValid(isValid);
    },
    [setIsPasswordValid],
  );

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (newChange !== confirmNewChange) {
      setError("error: inputs must match");
      return;
    }

    try {
      const response = await changeAccountDetail(
        detailToChange,
        restaurantId,
        newChange,
      );

      if (response) {
        if (detailToChange === "name") {
          localStorage.setItem("restaurant", JSON.stringify(response));
        } else {
          // redirect to logout
          localStorage.removeItem("authToken");
          localStorage.removeItem("restaurant");
          navigate("/");
        }
      }
    } catch (error) {
      alert(error.message);
    }

    onRequestClose(); // close modal on success
  };

  if (!isOpen) return null; // don't render if modal is closed

  return (
    <BaseModal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      width={checkWidth}
    >
      <div className="base-modal">
        <h1 className="ChangeAccountDetailsModal-header">
          change {detailToChange}
        </h1>
        {(detailToChange === "email" || detailToChange === "password") && (
          <p className="ChangeAccountDetailsModal-redirect-msg">
            you'll be redirected to login again upon saving
          </p>
        )}
        <form
          className="ChangeAccountDetailsModal-form"
          onSubmit={handleSubmit}
        >
          <div className="ChangeAccountDetailsModal-form-group">
            <label>new {detailToChange}</label>
            <input
              type={detailToChange === "password" ? "password" : "text"}
              placeholder={`enter new ${detailToChange}`}
              value={newChange}
              onChange={(e) => setNewChange(e.target.value)}
              required
            />
          </div>
          <div className="ChangeAccountDetailsModal-form-group">
            <label>confirm new {detailToChange}</label>
            <input
              type={detailToChange === "password" ? "password" : "text"}
              placeholder={`confirm new ${detailToChange}`}
              value={confirmNewChange}
              onChange={(e) => setConfirmNewChange(e.target.value)}
              required
            />
          </div>
          {detailToChange === "password" && (
            <PasswordPolicyChecker
              password={newChange}
              confirmPassword={confirmNewChange}
              onPasswordValidityChange={updatePasswordValidity}
            />
          )}
          {error && <p className="ChangeAccountDetailsModal-error">{error}</p>}
          <div className="ChangeAccountDetailsModal-btns">
            <button
              className="ChangeAccountDetailsModal-cancel-btn"
              onClick={onRequestClose}
            >
              cancel
            </button>
            <button
              className="ChangeAccountDetailsModal-submit-btn"
              type="submit"
              disabled={detailToChange === "password" && !isPasswordValid}
            >
              save change
            </button>
          </div>
        </form>
      </div>
    </BaseModal>
  );
};

export default ChangeAccountDetailsModal;
